import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Heading,
    VStack,
    useToast,
    Flex,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useColorMode,
    Button,
    Text,
    Spinner,
    Select,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    Collapse,
    Badge,
    Icon,
    Checkbox,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import Navigation from '../components/Navigation';
import axios from '../utils/axiosConfig';

const MONTH_NAMES = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const AdminUnbilledPage = () => {
    const { colorMode } = useColorMode();
    const toast = useToast();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [monthFilter, setMonthFilter] = useState('');
    const [expandedRows, setExpandedRows] = useState({});
    const [callDetails, setCallDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const [selectedRows, setSelectedRows] = useState({});
    const [billing, setBilling] = useState(false);
    const [confirmData, setConfirmData] = useState(null);
    const cancelRef = React.useRef();

    const fetchUnbilled = useCallback(async () => {
        try {
            setLoading(true);
            const params = monthFilter ? `?month=${monthFilter}` : '';
            const response = await axios.get(`/api/admin/unbilled${params}`);
            if (response.data.success) {
                setData(response.data.data);
                setSelectedRows({});
            }
        } catch (error) {
            console.error('Error fetching unbilled data:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch unbilled data.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [monthFilter, toast]);

    useEffect(() => {
        fetchUnbilled();
    }, [fetchUnbilled]);

    const rowKey = (row) => `${row.userId}-${row.year}-${row.month}`;

    const toggleRow = async (row) => {
        const key = rowKey(row);
        if (expandedRows[key]) {
            setExpandedRows(prev => ({ ...prev, [key]: false }));
            return;
        }
        setExpandedRows(prev => ({ ...prev, [key]: true }));
        if (!callDetails[key]) {
            setLoadingDetails(prev => ({ ...prev, [key]: true }));
            try {
                const response = await axios.get(`/api/admin/unbilled/${row.userId}/${row.year}/${row.month}`);
                if (response.data.success) {
                    setCallDetails(prev => ({ ...prev, [key]: response.data.data }));
                }
            } catch (error) {
                toast({ title: 'Error', description: 'Failed to fetch call details.', status: 'error', duration: 3000, isClosable: true });
            } finally {
                setLoadingDetails(prev => ({ ...prev, [key]: false }));
            }
        }
    };

    const toggleSelect = (row) => {
        const key = rowKey(row);
        setSelectedRows(prev => {
            const updated = { ...prev };
            if (updated[key]) {
                delete updated[key];
            } else {
                updated[key] = row;
            }
            return updated;
        });
    };

    const toggleSelectAllForUser = (userId) => {
        const userRows = data.filter(r => r.userId === userId);
        const allSelected = userRows.every(r => selectedRows[rowKey(r)]);
        setSelectedRows(prev => {
            const updated = { ...prev };
            userRows.forEach(r => {
                if (allSelected) {
                    delete updated[rowKey(r)];
                } else {
                    updated[rowKey(r)] = r;
                }
            });
            return updated;
        });
    };

    // Group selected rows by userId
    const getSelectedByUser = () => {
        const selected = Object.values(selectedRows);
        const byUser = {};
        selected.forEach(row => {
            if (!byUser[row.userId]) {
                byUser[row.userId] = { name: row.name, email: row.email, userId: row.userId, rows: [] };
            }
            byUser[row.userId].rows.push(row);
        });
        return byUser;
    };

    const selectedTotal = Object.values(selectedRows).reduce((sum, r) => sum + r.unbilledAmount, 0);
    const selectedCount = Object.keys(selectedRows).length;

    const handleBillSelected = () => {
        const byUser = getSelectedByUser();
        const userIds = Object.keys(byUser);

        // Check each user's total meets $10 minimum
        for (const userId of userIds) {
            const userTotal = byUser[userId].rows.reduce((sum, r) => sum + r.unbilledAmount, 0);
            if (userTotal < 10) {
                toast({
                    title: 'Below minimum',
                    description: `${byUser[userId].name}'s selected total ($${userTotal.toFixed(2)}) is below the $10.00 minimum. Please select more months for this user.`,
                    status: 'warning',
                    duration: 5000,
                    isClosable: true,
                });
                return;
            }
        }

        setConfirmData(byUser);
    };

    const handleConfirmBill = async () => {
        if (!confirmData) return;
        setBilling(true);
        const userIds = Object.keys(confirmData);
        let successCount = 0;
        let failCount = 0;

        for (const userId of userIds) {
            const userGroup = confirmData[userId];
            const periods = userGroup.rows.map(r => ({ year: r.year, month: r.month }));
            try {
                const response = await axios.put('/api/admin/mark-billed', { userId, periods });
                if (response.data.success) {
                    successCount++;
                    toast({
                        title: 'Invoice Created',
                        description: response.data.message,
                        status: 'success',
                        duration: 5000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                failCount++;
                toast({
                    title: 'Error',
                    description: error.response?.data?.message || `Failed to bill ${userGroup.name}`,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        }

        setBilling(false);
        setConfirmData(null);
        setSelectedRows({});
        if (successCount > 0) await fetchUnbilled();
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-SG', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const formatDuration = (ms) => {
        const totalSec = Math.round(ms / 1000);
        const min = Math.floor(totalSec / 60);
        const sec = totalSec % 60;
        return `${min}m ${sec}s`;
    };

    // Build month options from data
    const monthOptions = [...new Set(data.map(r => `${r.year}-${String(r.month).padStart(2, '0')}`))].sort().reverse();

    const grandTotal = data.reduce((sum, row) => sum + row.unbilledAmount, 0);
    const grandCalls = data.reduce((sum, row) => sum + row.totalCalls, 0);
    const grandMinutes = data.reduce((sum, row) => sum + row.totalMinutes, 0);

    // Group data by user for display
    const userIds = [...new Set(data.map(r => r.userId))];

    return (
        <Flex>
            <Navigation />
            <Box flex="1" p={6} maxW="1200px" mx="auto">
                <VStack spacing={6} align="stretch">
                    <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                        <Heading size="lg" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                            Unbilled Usage
                        </Heading>
                        <Flex gap={3} align="center">
                            <Select
                                size="sm"
                                w="200px"
                                value={monthFilter}
                                onChange={(e) => setMonthFilter(e.target.value)}
                                bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                color={colorMode === 'light' ? 'gray.800' : 'white'}
                                borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                            >
                                <option value="">All Months</option>
                                {monthOptions.map(opt => {
                                    const [y, m] = opt.split('-');
                                    return (
                                        <option key={opt} value={opt}>
                                            {MONTH_NAMES[parseInt(m)]} {y}
                                        </option>
                                    );
                                })}
                            </Select>
                            <Button
                                size="sm"
                                onClick={fetchUnbilled}
                                isLoading={loading}
                                colorScheme="blue"
                                variant="outline"
                            >
                                Refresh
                            </Button>
                        </Flex>
                    </Flex>

                    {/* Selection action bar */}
                    {selectedCount > 0 && (
                        <Box
                            p={3}
                            bg={selectedTotal >= 10
                                ? (colorMode === 'light' ? 'green.50' : 'green.900')
                                : (colorMode === 'light' ? 'orange.50' : 'orange.900')}
                            borderRadius="md"
                            borderWidth={1}
                            borderColor={selectedTotal >= 10
                                ? (colorMode === 'light' ? 'green.200' : 'green.700')
                                : (colorMode === 'light' ? 'orange.200' : 'orange.700')}
                        >
                            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                                <Text
                                    fontWeight="semibold"
                                    fontSize="sm"
                                    color={selectedTotal >= 10
                                        ? (colorMode === 'light' ? 'green.800' : 'green.200')
                                        : (colorMode === 'light' ? 'orange.800' : 'orange.200')}
                                >
                                    {selectedCount} period(s) selected — Total: ${selectedTotal.toFixed(2)}
                                    {selectedTotal < 10 && ' (minimum $10.00 per user required)'}
                                </Text>
                                <Flex gap={2}>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setSelectedRows({})}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        size="sm"
                                        colorScheme="green"
                                        onClick={handleBillSelected}
                                    >
                                        Bill &amp; Invoice Selected
                                    </Button>
                                </Flex>
                            </Flex>
                        </Box>
                    )}

                    {loading ? (
                        <Flex justify="center" py={10}>
                            <Spinner size="xl" />
                        </Flex>
                    ) : data.length === 0 ? (
                        <Box
                            p={8}
                            textAlign="center"
                            bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}
                            borderRadius="md"
                        >
                            <Text color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                No unbilled records found.
                            </Text>
                        </Box>
                    ) : (
                        <Box
                            borderWidth={1}
                            borderRadius="md"
                            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                            overflowX="auto"
                        >
                            <Table variant="simple" size="sm">
                                <Thead bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}>
                                    <Tr>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'} w="30px"></Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'} w="30px"></Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>User</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Month</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'} isNumeric>Calls</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'} isNumeric>Minutes</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'} isNumeric>Amount ($)</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {userIds.map(uid => {
                                        const userRows = data.filter(r => r.userId === uid);
                                        const userTotal = userRows.reduce((s, r) => s + r.unbilledAmount, 0);
                                        const allUserSelected = userRows.every(r => selectedRows[rowKey(r)]);
                                        const someUserSelected = userRows.some(r => selectedRows[rowKey(r)]);

                                        return (
                                            <React.Fragment key={uid}>
                                                {/* User header row */}
                                                <Tr bg={colorMode === 'light' ? 'gray.100' : 'gray.650'}>
                                                    <Td>
                                                        <Checkbox
                                                            isChecked={allUserSelected}
                                                            isIndeterminate={someUserSelected && !allUserSelected}
                                                            onChange={() => toggleSelectAllForUser(uid)}
                                                            colorScheme="green"
                                                            size="sm"
                                                        />
                                                    </Td>
                                                    <Td></Td>
                                                    <Td colSpan={3}>
                                                        <Text fontWeight="bold" fontSize="sm" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                            {userRows[0].name}
                                                        </Text>
                                                        <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                                            {userRows[0].email}
                                                        </Text>
                                                    </Td>
                                                    <Td isNumeric fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                                        {userRows.length} month(s)
                                                    </Td>
                                                    <Td isNumeric fontWeight="bold" fontSize="sm" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                        {userTotal.toFixed(2)}
                                                    </Td>
                                                </Tr>
                                                {/* Individual month rows */}
                                                {userRows.map(row => {
                                                    const key = rowKey(row);
                                                    const isExpanded = expandedRows[key];
                                                    const details = callDetails[key];
                                                    const isLoadingDetail = loadingDetails[key];
                                                    const isSelected = !!selectedRows[key];

                                                    return (
                                                        <React.Fragment key={key}>
                                                            <Tr
                                                                cursor="pointer"
                                                                onClick={() => toggleRow(row)}
                                                                _hover={{ bg: colorMode === 'light' ? 'gray.50' : 'gray.700' }}
                                                                bg={isSelected ? (colorMode === 'light' ? 'green.50' : 'green.900') : 'transparent'}
                                                            >
                                                                <Td onClick={e => e.stopPropagation()}>
                                                                    <Checkbox
                                                                        isChecked={isSelected}
                                                                        onChange={() => toggleSelect(row)}
                                                                        colorScheme="green"
                                                                        size="sm"
                                                                    />
                                                                </Td>
                                                                <Td>
                                                                    <Icon
                                                                        as={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                                                                        color={colorMode === 'light' ? 'gray.500' : 'gray.400'}
                                                                    />
                                                                </Td>
                                                                <Td></Td>
                                                                <Td>
                                                                    <Badge colorScheme="blue" variant="subtle">
                                                                        {MONTH_NAMES[row.month]} {row.year}
                                                                    </Badge>
                                                                </Td>
                                                                <Td isNumeric color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                                    {row.totalCalls}
                                                                </Td>
                                                                <Td isNumeric color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                                    {row.totalMinutes}
                                                                </Td>
                                                                <Td isNumeric fontWeight="semibold" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                                    {row.unbilledAmount.toFixed(2)}
                                                                </Td>
                                                            </Tr>
                                                            {/* Expandable call details */}
                                                            <Tr>
                                                                <Td colSpan={7} p={0} borderBottom={isExpanded ? '1px solid' : 'none'} borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}>
                                                                    <Collapse in={isExpanded} animateOpacity>
                                                                        <Box p={4} bg={colorMode === 'light' ? 'gray.25' : 'gray.750'}>
                                                                            {isLoadingDetail ? (
                                                                                <Flex justify="center" py={4}>
                                                                                    <Spinner size="sm" />
                                                                                </Flex>
                                                                            ) : details && details.length > 0 ? (
                                                                                <Table size="xs" variant="simple">
                                                                                    <Thead>
                                                                                        <Tr>
                                                                                            <Th fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>Date</Th>
                                                                                            <Th fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>From</Th>
                                                                                            <Th fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>To</Th>
                                                                                            <Th fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>Direction</Th>
                                                                                            <Th fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} isNumeric>Duration</Th>
                                                                                            <Th fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} isNumeric>Cost ($)</Th>
                                                                                        </Tr>
                                                                                    </Thead>
                                                                                    <Tbody>
                                                                                        {details.map((call) => (
                                                                                            <Tr key={call._id || call.callSid}>
                                                                                                <Td fontSize="xs" color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                                                                                                    {formatDate(call.createdAt)}
                                                                                                </Td>
                                                                                                <Td fontSize="xs" color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>{call.from}</Td>
                                                                                                <Td fontSize="xs" color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>{call.to}</Td>
                                                                                                <Td fontSize="xs">
                                                                                                    <Badge size="sm" colorScheme={call.direction === 'inbound' ? 'blue' : 'orange'} variant="subtle">
                                                                                                        {call.direction}
                                                                                                    </Badge>
                                                                                                </Td>
                                                                                                <Td fontSize="xs" isNumeric color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                                                                                                    {formatDuration(call.duration)}
                                                                                                </Td>
                                                                                                <Td fontSize="xs" isNumeric color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                                                                                                    {call.totalCost.toFixed(4)}
                                                                                                </Td>
                                                                                            </Tr>
                                                                                        ))}
                                                                                    </Tbody>
                                                                                </Table>
                                                                            ) : (
                                                                                <Text fontSize="sm" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                                                                    No call records found.
                                                                                </Text>
                                                                            )}
                                                                        </Box>
                                                                    </Collapse>
                                                                </Td>
                                                            </Tr>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                    {/* Grand total row */}
                                    <Tr bg={colorMode === 'light' ? 'gray.100' : 'gray.600'}>
                                        <Td></Td>
                                        <Td></Td>
                                        <Td fontWeight="bold" color={colorMode === 'light' ? 'gray.800' : 'white'}>Grand Total</Td>
                                        <Td></Td>
                                        <Td isNumeric fontWeight="bold" color={colorMode === 'light' ? 'gray.800' : 'white'}>{grandCalls}</Td>
                                        <Td isNumeric fontWeight="bold" color={colorMode === 'light' ? 'gray.800' : 'white'}>{grandMinutes.toFixed(2)}</Td>
                                        <Td isNumeric fontWeight="bold" color={colorMode === 'light' ? 'gray.800' : 'white'}>{grandTotal.toFixed(2)}</Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </Box>
                    )}
                </VStack>
            </Box>

            {/* Confirmation Dialog */}
            <AlertDialog
                isOpen={!!confirmData}
                leastDestructiveRef={cancelRef}
                onClose={() => setConfirmData(null)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent bg={colorMode === 'light' ? 'white' : 'gray.800'}>
                        <AlertDialogHeader color={colorMode === 'light' ? 'gray.800' : 'white'}>
                            Bill &amp; Send Invoice
                        </AlertDialogHeader>
                        <AlertDialogBody color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                            {confirmData && Object.values(confirmData).map(group => {
                                const userTotal = group.rows.reduce((s, r) => s + r.unbilledAmount, 0);
                                const periodList = group.rows.map(r => `${MONTH_NAMES[r.month]} ${r.year}`).join(', ');
                                return (
                                    <Box key={group.userId} mb={3}>
                                        <Text fontWeight="bold">{group.name}</Text>
                                        <Text fontSize="sm">Periods: {periodList}</Text>
                                        <Text fontSize="sm">Amount: <strong>${userTotal.toFixed(2)}</strong></Text>
                                        <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                            Invoice email will be sent to {group.email}
                                        </Text>
                                    </Box>
                                );
                            })}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setConfirmData(null)}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="green"
                                onClick={handleConfirmBill}
                                ml={3}
                                isLoading={billing}
                            >
                                Confirm &amp; Send
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Flex>
    );
};

export default AdminUnbilledPage;
